import { useEffect, useState } from "react";
import { Button, FormInput, FormSelect, FormTextarea, Modal } from "../../../components/ui";
import { SearchableSelect } from "../../../components/ui/SearchableSelect";
import toast from "react-hot-toast";

const HARDCODED_ROOMS = [
  "Foyer / Entrance",
  "Living Room",
  "Family Room",
  "Dining Room",
  "Kitchen",
  "Pantry",
  "Master Bedroom",
  "Bedroom",
  "Guest Bedroom",
  "Children's Bedroom",
  "Nursery",
  "Bathroom",
  "Powder Room",
  "Study / Home Office",
  "Library",
  "Pooja Room",
  "Walk-in Closet",
  "Laundry Room",
  "Utility Room",
  "Storage Room",
  "Balcony",
  "Patio",
  "Sunroom",
  "Home Theater",
  "Game Room",
  "Gym",
  "Basement",
  "Attic",
  "Loft",
  "Garage",
  "Workshop"
].map(room => ({ label: room, value: room }));

const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  status: "Active",
};

export default function AddRoomModal({ isOpen, onClose, onSave, initialData, isViewOnly, existingRooms = [] }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setErrorMsg("");
    if (!initialData) {
      setFormData(EMPTY_FORM);
      return;
    }
    setFormData({
      name: initialData.name || "",
      code: initialData.code || "",
      description: initialData.description || "",
      status: initialData.status || "Active",
    });
  }, [initialData, isOpen]);

  const updateField = (name, value) => {
    setErrorMsg("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name) return;

    if (existingRooms.length > 0) {
      const isDuplicate = existingRooms.some(r => (r.name || r).toLowerCase() === formData.name.toLowerCase());
      if (isDuplicate) {
        setErrorMsg("Room already exists");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        name: formData.name.trim(),
        code: formData.code.trim(),
        description: formData.description.trim(),
      });
    } catch (error) {
      // If the parent passes back an error that it already exists
      if (error?.response?.data?.message?.toLowerCase().includes("exist")) {
        setErrorMsg("Room already exists");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex flex-col gap-0.5 z-10 relative">
          <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
            BLUM INTERIORS
          </span>
          <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
            {isViewOnly ? "View" : initialData ? "Modify" : "Add"} Room Requirement
          </span>
        </div>
      }
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 min-h-[300px] flex flex-col justify-between">
        <div className="grid grid-cols-1 gap-3">
          <SearchableSelect
            label={
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700 tracking-wider">
                <span className="text-slate-400"></span> ROOM NAME *
              </div>
            }
            value={formData.name}
            onChange={(val) => updateField("name", val)}
            options={HARDCODED_ROOMS}
            placeholder="Select a room..."
            error={errorMsg}
            readOnly={isViewOnly}
            allowCustom={true}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-4 border-t border-slate-100 mt-auto">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-semibold transition-all duration-300 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isViewOnly ? "Close" : "Cancel"}
          </button>
          {!isViewOnly && (
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:flex-1 flex items-center justify-center gap-2 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out border border-slate-900 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Adding..." : "Add Room"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
